import React from "react";
import { api } from "./api.jsx";
import { gameLabel } from "./data.jsx";
import { useConfirm } from "./chrome.jsx";

const { Button, Card, Tag, EmptyState } = window.CardboardmarketDesignSystem_2a3239;

/**
 * Sets sellers told us we are missing.
 *
 * WHERE THESE COME FROM. The posting form used to offer a text box when the set
 * somebody was holding was not among the chips, and that box wrote its value
 * into the LISTING — where no filter matches it, no import reconciles it, and
 * the gap it described was invisible to us. It is a report now, and this screen
 * is the other end of it: "I prefer everything modeled than having a bunch of
 * free text."
 *
 * GROUPED BY THE SET, NOT BY THE REPORT, because that is the shape of the work.
 * Adding a set to the catalogue is one action however many people asked for it,
 * and five sellers hitting the same gap is the strongest signal on this screen —
 * a flat list of reports buries that under timestamps.
 *
 * NOT TRANSLATED, like every operator screen here.
 */
export function SetReports() {
  const [state, setState] = React.useState({ loading: true, sets: [], error: null });
  const [busy, setBusy] = React.useState(null);
  const [confirm, confirmDialog] = useConfirm();

  const load = React.useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const r = await api.adminSetReports();
      setState({ loading: false, sets: r.sets || [], error: null });
    } catch (e) {
      setState({ loading: false, sets: [], error: e });
    }
  }, []);

  React.useEffect(() => { void load(); }, [load]);

  const handle = async (set, resolution) => {
    /*
     * Asked first (the owner, 2026-10-23: "confirmation should be added
     * anywhere we're changing something kind of important"): either answer
     * closes every open report naming the set, for every seller who sent one,
     * and nothing on this screen reopens them.
     */
    const reports = `${set.reports} report${set.reports === 1 ? '' : 's'}`;
    if (!(await confirm(resolution === 'added'
      ? {
        title: `Mark "${set.saidSet}" as added?`,
        description: `The ${reports} naming it close, counted as a gap the catalogue had.`,
        confirmLabel: 'Mark it added',
      }
      : {
        title: `Dismiss "${set.saidSet}"?`,
        description: `The ${reports} naming it close, counted as a set we already held.`,
        confirmLabel: 'Dismiss the reports',
      }))) return;
    setBusy(`${set.saidSet} ${set.game ?? ''}`);
    try {
      await api.adminHandleSetReport(set.saidSet, set.game, resolution);
      /*
       * Reloaded rather than removed in place: marking one gap done closes every
       * open report naming it, and predicting which rows those were is harder to
       * reason about than asking the server again.
       */
      await load();
    } catch (e) {
      setState((s) => ({ ...s, error: e }));
    } finally {
      setBusy(null);
    }
  };

  if (state.loading) return <p style={{ font: 'var(--type-body)' }}>Loading the queue...</p>;

  if (state.error) {
    return (
      <EmptyState icon="alert-triangle" title="The queue would not load"
        description={String(state.error?.message || state.error)}
        action={<Button onClick={load}>Try again</Button>} />
    );
  }

  if (!state.sets.length) {
    return (
      <EmptyState icon="check" title="Nothing missing"
        description="No seller has reported a set we do not hold. This is the normal state." />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      {confirmDialog}
      <p style={{ font: 'var(--type-caption)', margin: 0 }}>
        {/* Most reported first, because how many people hit a gap is what decides
            which one to close next. */}
        {state.sets.length} set{state.sets.length === 1 ? '' : 's'} sellers could not find,
        most reported first.
      </p>
      {state.sets.map((set) => {
        const key = `${set.saidSet} ${set.game ?? ''}`;
        return (
          <Card key={key} padding="var(--space-5)">
            <div style={{ display: 'flex', gap: 'var(--space-4)', alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', flexWrap: 'wrap' }}>
                  <strong style={{ font: 'var(--type-ui)' }}>{set.saidSet}</strong>
                  {set.game ? <Tag tone="neutral">{gameLabel(set.game)}</Tag> : <Tag tone="warning">no game</Tag>}
                  <Tag tone={set.reports > 1 ? 'brand' : 'neutral'}>
                    {set.reports} report{set.reports === 1 ? '' : 's'}
                  </Tag>
                </div>
                {/*
                  WHICH CARDS IT WAS REPORTED AGAINST. "Unleashed" means nothing
                  on its own; "reported against Leblanc, Deceiver, and our
                  catalogue says Unleashed" is something somebody can go and check
                  against the real card.
                */}
                {set.cards.length ? (
                  <p style={{ font: 'var(--type-caption)', margin: 0 }}>
                    against {set.cards.map((c) => c.name || `#${c.productId}`).join(', ')}
                    {set.cards[0]?.expansion ? ` - our catalogue says ${set.cards[0].expansion}` : ''}
                  </p>
                ) : (
                  <p style={{ font: 'var(--type-caption)', margin: 0, opacity: 0.7 }}>
                    no card attached
                  </p>
                )}
                <p style={{ font: 'var(--type-caption)', margin: 0, opacity: 0.7 }}>
                  last reported {new Date(set.lastSeen).toLocaleDateString()}
                </p>
              </div>
              {/*
                TWO ENDINGS, because a report can be wrong: "the user could be
                wrong and we could already be right."

                They are not the same act dressed differently. "Added" says the
                catalogue was short and the seller was right. "We already had it"
                says it was not — which almost always means the set IS there under
                a name the seller did not recognise, and that is a finding about
                OUR names rather than about the person. Counted apart, those two
                say opposite things about what to do next: ingest more sets, or
                look at what we call the ones we have.

                Both close every open report naming the set, because either answer
                is true for all of them at the same moment.
              */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                <Button variant="secondary" size="sm" disabled={busy === key}
                  onClick={() => handle(set, 'added')}>
                  {busy === key ? 'Marking...' : 'Added to the catalogue'}
                </Button>
                <Button variant="ghost" size="sm" disabled={busy === key}
                  onClick={() => handle(set, 'dismissed')}>
                  We already had it
                </Button>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}
